import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { CURRENCIES, FAMILY_LABELS, FAMILY_SUBTYPES, SUBTYPE_LABELS, familyOf, familyOfSub, type Account, type AccountFamily, type AccountSubtype, type Currency } from "@/lib/finance";
import { MoneyInput, moneyInputValue, parseMoney } from "@/components/MoneyInput";
import { AssetFields, EMPTY_ASSET, NEW_LINK, assetFromAccount, assetPayload } from "@/components/accounts/AssetFields";
import type { NewLoan } from "@/components/accounts/LoanBudgetPrompt";

const LOAN_SUBS: AccountSubtype[] = ["mortgage", "auto", "personal"];

const DEFAULT_SUB: Record<AccountFamily, AccountSubtype> = { debit: "checking", credit: "card", investment: "capital", property: "house" };
const EMPTY = { family: "debit" as AccountFamily, subtype: "checking" as AccountSubtype, currency: "MXN" as Currency, name: "", opening_balance: "", credit_limit: "", statement_day: "", payment_day: "", loan_monthly_payment: "", notes: "", linked_asset_id: "none", ...EMPTY_ASSET };
type FormState = typeof EMPTY;
const LOAN_FOR: Record<string, AccountSubtype> = { house: "mortgage", land: "mortgage", vehicle: "auto" };
const ASSET_FOR: Record<string, AccountSubtype> = { mortgage: "house", auto: "vehicle" };
const loanSeed = (f: FormState): Partial<FormState> => {
  const subtype = LOAN_FOR[f.subtype] ?? "personal";
  return { family: "credit", subtype, name: `${subtype === "mortgage" ? "Hipoteca" : "Crédito"} ${f.name.trim()}`.trim() };
};

function LinkedAsset({ value, assets, onChange, onCreate }: { value: string; assets: Account[]; onChange: (v: string) => void; onCreate?: () => void }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-700">Bien ligado (opcional)</Label>
      <Select value={value} onValueChange={(v: string) => { if (!v) return; if (v === NEW_LINK) onCreate?.(); else onChange(v); }}>
        <SelectTrigger className="h-11" data-testid="account-form-linked-asset"><SelectValue>{assets.find((a) => a.id === value)?.name ?? "Sin bien"}</SelectValue></SelectTrigger>
        <SelectContent className="bg-white">
          <SelectItem value="none">Sin bien</SelectItem>
          {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          {onCreate && <SelectItem value={NEW_LINK} className="font-semibold text-[#2C4F91]" data-testid="linked-asset-create">+ Agregar este bien</SelectItem>}
        </SelectContent>
      </Select>
    </div>
  );
}

const fromAccount = (a: Account): FormState => {
  const family = familyOf(a.account_type);
  return {
    family, subtype: a.account_subtype ?? DEFAULT_SUB[family], currency: a.currency ?? "MXN", name: a.name,
    opening_balance: moneyInputValue(a.is_liability ? Math.abs(a.opening_balance) : a.opening_balance),
    credit_limit: moneyInputValue(a.credit_limit), statement_day: a.statement_day?.toString() ?? "", payment_day: a.payment_day?.toString() ?? "",
    loan_monthly_payment: moneyInputValue(a.loan_monthly_payment),
    notes: a.notes ?? "", linked_asset_id: a.meta?.linked_asset?.id ?? "none",
    ...assetFromAccount(a),
  };
};

const num = (v: string) => (v.trim() === "" ? null : parseMoney(v));

function Field({ id, label, value, onChange, day, disabled, hint, money }: { id: string; label: string; value: string; onChange: (v: string) => void; day?: boolean; disabled?: boolean; hint?: string; money?: boolean }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm text-slate-700">{label}</Label>
      {money ? (
        <MoneyInput id={id} value={value} onChange={onChange} disabled={disabled} className="h-11" testId={`account-form-${id}`} />
      ) : (
        <Input id={id} type="text" inputMode={day ? "numeric" : undefined} value={value} disabled={disabled} placeholder={day ? "1 a 31" : undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(day ? e.target.value.replace(/\D/g, "").slice(0, 2) : e.target.value)}
          className="h-11" data-testid={`account-form-${id}`} />
      )}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function FamilyTabs({ value, onChange, disabled, only }: { value: AccountFamily; onChange: (f: AccountFamily) => void; disabled: boolean; only?: AccountFamily[] }) {
  const fams = only ?? (Object.keys(FAMILY_LABELS) as AccountFamily[]);
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(fams.length, 4)}, minmax(0, 1fr))` }} data-testid="account-form-family">
      {fams.map((f) => (
        <button key={f} type="button" disabled={disabled && f !== value} onClick={() => onChange(f)}
          className={`h-11 rounded-md border text-sm font-semibold transition-colors disabled:opacity-40 ${value === f ? "border-[#1B2A4A] bg-[#1B2A4A] text-white" : "border-[#DDE1E8] text-[#1B2A4A] hover:bg-[#F4F5F7]"}`}
          data-testid={`account-family-${f}`}>{FAMILY_LABELS[f]}</button>
      ))}
    </div>
  );
}

type DialogProps = { open: boolean; onOpenChange: (o: boolean) => void; account: Account | null; onSaved: (loan?: NewLoan) => void; preset?: AccountFamily;
  loans?: Account[]; assets?: Account[]; seed?: Partial<FormState>; nested?: boolean; onCreated?: (a: Account, loan?: NewLoan) => void; onLoanPrompt?: (l: NewLoan) => void;
  allowed?: AccountSubtype[] };

export function AccountFormDialog({ open, onOpenChange, account, onSaved, preset, loans = [], assets = [], seed, nested, onCreated, onLoanPrompt, allowed }: DialogProps) {
  const [f, setF] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [child, setChild] = useState<Partial<FormState> | null>(null);
  const [extra, setExtra] = useState<Account[]>([]);
  const set = (k: keyof FormState) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const credit = f.family === "credit";
  const loan = credit && LOAN_SUBS.includes(f.subtype);
  const locked = !!account?.has_transactions;
  const allLoans = [...loans, ...extra.filter((a) => a.account_type === "loan" && !loans.some((l) => l.id === a.id))];
  const allAssets = [...assets, ...extra.filter((a) => a.account_type === "property" && !assets.some((l) => l.id === a.id))];
  const onlyFams = allowed?.length ? [...new Set(allowed.map(familyOfSub))] : undefined;
  const firstSub = (fam: AccountFamily) => (allowed?.length ? FAMILY_SUBTYPES[fam].find((s) => allowed.includes(s)) : undefined) ?? DEFAULT_SUB[fam];
  const subOptions = FAMILY_SUBTYPES[f.family].filter((s) => !allowed?.length || allowed.includes(s));

  useEffect(() => {
    if (!open) return;
    setExtra([]);
    const fam = allowed?.length ? familyOfSub(allowed[0]) : undefined;
    setF(account ? fromAccount(account) : seed ? { ...EMPTY, ...seed } : fam ? { ...EMPTY, family: fam, subtype: allowed![0] } : preset ? { ...EMPTY, family: preset, subtype: DEFAULT_SUB[preset] } : EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account, preset, seed]);

  const childCreated = (a: Account, nl?: NewLoan) => {
    setExtra((x) => [...x, a]);
    setF((s) => (a.account_type === "loan" ? { ...s, linked_loan_id: a.id, paying: "yes" } : { ...s, linked_asset_id: a.id }));
    if (nl) onLoanPrompt?.(nl);
  };

  const linkAsset = async (loanId: string) => {
    const before = account?.meta?.linked_asset?.id ?? "none";
    if (!ASSET_FOR[f.subtype] || !f.linked_asset_id || f.linked_asset_id === before) return;
    if (before !== "none") await api.put(`/accounts/${before}`, { linked_loan_id: null });
    if (f.linked_asset_id !== "none") await api.put(`/accounts/${f.linked_asset_id}`, { linked_loan_id: loanId });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("El nombre es obligatorio");
    if (Number.isNaN(parseMoney(f.opening_balance))) return toast.error("Indica el saldo");
    if (f.family === "property" && !account && !f.paying) return toast.error("Dinos si todavía lo estás pagando con un crédito");
    if (credit && !loan && num(f.credit_limit) === null) return toast.error("Indica el límite de crédito");
    const payload = {
      name: f.name.trim(), account_subtype: f.subtype, currency: f.currency, opening_balance: parseMoney(f.opening_balance),
      credit_limit: credit && !loan ? num(f.credit_limit) : null,
      statement_day: credit && !loan ? num(f.statement_day) : null, payment_day: credit ? num(f.payment_day) : null,
      loan_monthly_payment: loan ? num(f.loan_monthly_payment) : null,
      notes: f.notes.trim() || null,
      ...(f.family === "property" ? assetPayload(f, !account) : {}),
    };
    setSaving(true);
    try {
      let created: Account | null = null;
      if (account) await api.put(`/accounts/${account.id}`, payload);
      else created = (await api.post<Account>("/accounts", payload)).data;
      if (loan) await linkAsset(account?.id ?? created!.id);
      toast.success(account ? "Cuenta actualizada" : "Cuenta creada");
      onOpenChange(false);
      const nl = created && loan && created.loan_monthly_payment ? { id: created.id, name: created.name, monthly: created.loan_monthly_payment } : undefined;
      if (nested && created) onCreated?.(created, nl);
      else onSaved(nl);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const balanceLabel = f.family === "debit" ? "Saldo inicial" : credit ? "Saldo (lo que debes hoy)" : f.family === "property" ? "Valor estimado" : "Saldo";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="account-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1B2A4A]">{account ? "Editar cuenta" : nested && !allowed ? (f.family === "property" ? "Agregar el bien" : "Crear el crédito") : nested ? "Crear cuenta" : "Agregar cuenta"}</DialogTitle>
          <DialogDescription>Hipotecas, créditos automotrices, personales y departamentales se registran como Crédito.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="account-form">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Tipo de cuenta</Label>
            <FamilyTabs value={f.family} disabled={!!account} only={onlyFams} onChange={(fam) => setF((s) => ({ ...s, family: fam, subtype: firstSub(fam) }))} />
          </div>
          <Field id="name" label="Nombre" value={f.name} onChange={set("name")} />
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Tipo</Label>
            <Select key={f.family} value={f.subtype} onValueChange={set("subtype")}>
              <SelectTrigger className="h-11" data-testid="account-form-subtype"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {subOptions.map((s) => <SelectItem key={s} value={s} data-testid={`account-subtype-option-${s}`}>{SUBTYPE_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Moneda</Label>
            <Select value={f.currency} onValueChange={(v: string) => set("currency")(v)} disabled={locked}>
              <SelectTrigger className="h-11" data-testid="account-form-currency"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {CURRENCIES.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`account-currency-option-${c.value}`}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {locked ? <p className="text-xs text-slate-400">Ya tiene movimientos: su moneda no se puede cambiar.</p>
              : f.currency !== "MXN" && <p className="text-xs text-slate-400">Escribe los montos en {f.currency}; la app los convierte a pesos con el tipo de cambio del día.</p>}
          </div>
          {credit && !loan && (
            <div className="grid gap-4 sm:grid-cols-3">
              <Field id="credit_limit" money label="Límite de crédito" value={f.credit_limit} onChange={set("credit_limit")} />
              <Field id="statement_day" day label="Día de corte" value={f.statement_day} onChange={set("statement_day")} />
              <Field id="payment_day" day label="Día de vencimiento" value={f.payment_day} onChange={set("payment_day")} />
            </div>
          )}
          {loan && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="loan_monthly_payment" money label="Mensualidad" value={f.loan_monthly_payment} onChange={set("loan_monthly_payment")} />
              <Field id="payment_day" day label="Día de pago" value={f.payment_day} onChange={set("payment_day")} />
            </div>
          )}
          {loan && ASSET_FOR[f.subtype] && <LinkedAsset value={f.linked_asset_id} assets={allAssets} onChange={set("linked_asset_id")}
            onCreate={nested ? undefined : () => setChild({ family: "property", subtype: ASSET_FOR[f.subtype], paying: "no" })} />}
          <Field id="opening_balance" money label={balanceLabel} value={f.opening_balance} onChange={set("opening_balance")} disabled={locked}
            hint={locked ? (f.family === "property" ? "Ya tiene movimientos: usa “Actualizar valor”." : "Ya tiene movimientos: para corregir el saldo usa “Registrar ajuste”.") : credit ? "Escribe la deuda en positivo." : undefined} />
          {f.family === "property" && <AssetFields f={f} set={(k, v) => setF((s) => ({ ...s, [k]: v }))} isNew={!account} loans={allLoans}
            onCreateLoan={nested ? undefined : () => setChild(loanSeed(f))} />}
          <div className="space-y-1.5">
            <Label htmlFor="notes" className="text-sm text-slate-700">Notas (opcional)</Label>
            <Textarea id="notes" value={f.notes} maxLength={500} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set("notes")(e.target.value)} placeholder="Ej. tasa, plazo o fecha de término" data-testid="account-form-notes" />
          </div>
          <Button type="submit" disabled={saving} className="h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="account-form-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : account ? "Guardar cambios" : "Agregar cuenta"}
          </Button>
        </form>
      </DialogContent>
      {!nested && <AccountFormDialog open={!!child} onOpenChange={(o) => { if (!o) setChild(null); }} account={null} onSaved={() => undefined}
        seed={child ?? undefined} nested onCreated={childCreated} />}
    </Dialog>
  );
}
